import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, CheckCircle, AlertCircle, ArrowRight, Clock, 
  Calendar, DollarSign, Calculator, AlertTriangle,
  ChevronDown, ChevronUp, Zap, Star, HelpCircle,
  Download, Shield, TrendingUp
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const DeclaracaoAnualMeiPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [faturamento, setFaturamento] = useState('');
  const [resultado, setResultado] = useState(null);

  const navigateTo = (url) => {
    window.location.href = url;
  };

  // Calculadora simples
  const calcularSituacao = () => {
    const valor = parseFloat(faturamento.replace(/\D/g, '')) / 100;
    if (valor > 81000) {
      setResultado({
        status: 'excedeu',
        mensagem: 'Seu faturamento excedeu o limite do MEI!',
        detalhe: 'Você precisa migrar para ME (Microempresa). Entre em contato conosco para ajuda.'
      });
    } else if (valor > 64800) {
      setResultado({
        status: 'atencao',
        mensagem: 'Atenção! Você está próximo do limite.',
        detalhe: `Faltam R$ ${(81000 - valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} para atingir o limite de R$ 81.000,00`
      });
    } else {
      setResultado({
        status: 'ok',
        mensagem: 'Tudo certo! Você está dentro do limite.',
        detalhe: `Você pode faturar mais R$ ${(81000 - valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} este ano.`
      });
    }
  };

  // Passo a passo
  const steps = [
    {
      number: '01',
      title: 'Acesse o Portal do Simples Nacional',
      description: 'Vá até o site gov.br ou use o MeuMei para acessar diretamente.',
      dica: 'Com MeuMei, fazemos tudo automaticamente!'
    },
    {
      number: '02',
      title: 'Informe o CNPJ',
      description: 'Digite o número do seu CNPJ (apenas números, sem pontos ou traços).',
      dica: 'Confira se o CNPJ está correto antes de prosseguir.'
    },
    {
      number: '03',
      title: 'Selecione o ano-calendário',
      description: 'Escolha o ano que você vai declarar (ex: 2024 para declarar em 2025).',
      dica: 'A declaração deve ser feita até 31 de maio.'
    },
    {
      number: '04',
      title: 'Informe o faturamento bruto',
      description: 'Preencha o valor total que você faturou no ano, separado por atividade.',
      dica: 'Some todas as notas fiscais + vendas sem nota.'
    },
    {
      number: '05',
      title: 'Informe se teve empregado',
      description: 'Marque SIM ou NÃO se você teve funcionário registrado durante o ano.',
      dica: 'MEI pode ter no máximo 1 funcionário.'
    },
    {
      number: '06',
      title: 'Transmita a declaração',
      description: 'Confira os dados e clique em Transmitir. Guarde o recibo!',
      dica: 'Imprima ou salve o PDF do recibo de entrega.'
    }
  ];

  // Prazos importantes
  const prazos = [
    {
      data: '31 de Maio',
      evento: 'Prazo final para entregar DASN-SIMEI',
      status: 'importante',
      multa: 'Multa de R$ 50,00 se atrasar'
    },
    {
      data: 'Todo dia 20',
      evento: 'Vencimento do DAS mensal',
      status: 'mensal',
      multa: 'Juros e multa por atraso'
    },
    {
      data: '31 de Dezembro',
      evento: 'Fim do ano-calendário',
      status: 'info',
      multa: 'Some seu faturamento total'
    }
  ];

  // FAQ
  const faqs = [
    {
      pergunta: 'O que é DASN-SIMEI?',
      resposta: 'DASN-SIMEI é a Declaração Anual do Simples Nacional do Microempreendedor Individual. É um documento obrigatório onde você informa à Receita Federal quanto faturou no ano anterior. Deve ser entregue até 31 de maio de cada ano.'
    },
    {
      pergunta: 'Qual o prazo para entregar a declaração anual do MEI?',
      resposta: 'O prazo é até 31 de maio de cada ano. Por exemplo, a declaração referente ao ano de 2024 deve ser entregue até 31 de maio de 2025. Se você abriu o MEI durante o ano, também precisa declarar (proporcional aos meses).'
    },
    {
      pergunta: 'O que acontece se eu não entregar a declaração?',
      resposta: 'Se você não entregar a DASN-SIMEI no prazo, terá que pagar uma multa mínima de R$ 50,00 (ou 2% ao mês sobre o valor declarado, o que for maior). Além disso, seu MEI pode ficar com pendências na Receita Federal, impedindo a emissão de certidões.'
    },
    {
      pergunta: 'MEI que não faturou nada precisa declarar?',
      resposta: 'Sim! Mesmo que você não tenha faturado nada durante o ano, é obrigatório entregar a DASN-SIMEI informando faturamento R$ 0,00. A não entrega gera multa mesmo sem faturamento.'
    },
    {
      pergunta: 'Como saber quanto eu faturei no ano?',
      resposta: 'Some todas as vendas e serviços que você realizou, com ou sem nota fiscal. Use seu controle financeiro, extratos bancários e notas fiscais emitidas. Com o MeuMei, esse cálculo é automático!'
    },
    {
      pergunta: 'Qual o limite de faturamento do MEI em 2025?',
      resposta: 'O limite de faturamento do MEI em 2025 continua sendo R$ 81.000,00 por ano (média de R$ 6.750,00 por mês). Se você ultrapassar esse limite, precisará migrar para ME (Microempresa).'
    },
    {
      pergunta: 'Posso retificar a declaração depois de enviada?',
      resposta: 'Sim! Se você errou alguma informação, pode fazer uma declaração retificadora a qualquer momento. Basta acessar o sistema, selecionar "Retificadora" e corrigir os dados.'
    },
    {
      pergunta: 'MEI que foi baixado precisa declarar?',
      resposta: 'Sim! Se você deu baixa no MEI durante o ano, precisa entregar a Declaração de Extinção. O sistema perguntará a data de extinção e você informará o faturamento até aquela data.'
    }
  ];

  // Erros comuns
  const errosComuns = [
    {
      erro: 'Informar faturamento errado',
      solucao: 'Use o MeuMei para ter controle exato do seu faturamento'
    },
    {
      erro: 'Perder o prazo (31 de maio)',
      solucao: 'Configure lembretes ou deixe o MeuMei te avisar'
    },
    {
      erro: 'Esquecer vendas sem nota fiscal',
      solucao: 'Registre TODAS as vendas, mesmo sem nota'
    },
    {
      erro: 'Não guardar o recibo de entrega',
      solucao: 'Sempre salve o PDF do recibo como comprovante'
    }
  ];

  return (
    <div className="min-h-screen font-sans bg-white">
      {/* Schema.org para SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Declaração Anual MEI (DASN-SIMEI): Como Fazer em 2025",
          "description": "Guia completo para fazer a Declaração Anual do MEI. Passo a passo, prazos, multas e dicas para não errar.",
          "author": { "@type": "Organization", "name": "MeuMei" },
          "publisher": { "@type": "Organization", "name": "MeuMei" },
          "datePublished": "2025-01-01",
          "dateModified": "2025-01-01"
        })
      }} />

      <Header />

      {/* Hero Section */}
      <section 
        className="relative pt-20 pb-16 md:pt-32 md:pb-24 px-4 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 50%, #6D28D9 100%)',
        }}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, #ffffff 0%, transparent 70%)' }}
          />
        </div>

        <div className="max-w-4xl mx-auto relative z-10">
          {/* Breadcrumb */}
          <nav className="text-white/70 text-sm mb-6">
            <a href="/" className="hover:text-white">Início</a>
            <span className="mx-2">›</span>
            <a href="/blog" className="hover:text-white">Blog</a>
            <span className="mx-2">›</span>
            <span className="text-white">Declaração Anual MEI</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6"
            >
              <Calendar className="w-4 h-4" />
              Prazo: 31 de Maio
            </motion.span>

            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Declaração Anual MEI<br />
              <span className="text-yellow-300">(DASN-SIMEI)</span>
            </h1>
            
            <p className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed opacity-90 mb-8">
              Guia completo para fazer sua Declaração Anual do MEI em 2025. 
              Passo a passo, prazos, multas e como evitar erros!
            </p>

            <div className="flex flex-wrap justify-center gap-4 text-sm opacity-80">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                10 min de leitura
              </span>
              <span className="flex items-center gap-1">
                <Star className="w-4 h-4" />
                Atualizado Jan/2025
              </span>
            </div>
          </motion.div>
        </div>

        {/* Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* CTA Flutuante Mobile */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 shadow-lg z-40 md:hidden">
        <motion.button
          onClick={() => navigateTo('https://app.meumei.net/register')}
          whileTap={{ scale: 0.98 }}
          className="w-full py-4 bg-gradient-to-r from-purple-500 to-violet-600 text-white font-bold rounded-xl flex items-center justify-center gap-2"
        >
          <FileText className="w-5 h-5" />
          Fazer Declaração com MeuMei
        </motion.button>
      </div>

      {/* Conteúdo Principal */}
      <article className="py-12 md:py-20 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Alerta de Prazo */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-2xl p-6 mb-12"
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-amber-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-amber-800 text-xl mb-2">
                  ⚠️ Prazo até 31 de Maio de 2025!
                </h3>
                <p className="text-amber-700 mb-3">
                  A declaração do ano de 2024 deve ser entregue até <strong>31 de maio de 2025</strong>. 
                  Quem perder o prazo paga multa de no mínimo <strong>R$ 50,00</strong>.
                </p>
                <a 
                  href="https://app.meumei.net/register"
                  className="inline-flex items-center gap-2 text-amber-700 font-semibold hover:text-amber-800"
                >
                  Fazer minha declaração agora <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </motion.div>

          {/* Introdução */}
          <section className="prose prose-lg max-w-none mb-16">
            <p className="text-xl text-gray-600 leading-relaxed">
              A <strong>DASN-SIMEI</strong> (Declaração Anual do Simples Nacional do Microempreendedor Individual) 
              é uma obrigação de todo MEI, mesmo que você não tenha faturado nada. Neste guia, você vai aprender 
              <strong> como fazer a declaração passo a passo</strong>, conhecer os prazos e evitar multas.
            </p>
          </section>

          {/* O que é DASN */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
              O que é a Declaração Anual do MEI?
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              A <strong>DASN-SIMEI</strong> é o documento onde você informa à Receita Federal quanto sua 
              empresa faturou no ano anterior. É através dela que o governo acompanha se você está 
              dentro do limite de faturamento do MEI (R$ 81.000,00/ano).
            </p>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-purple-50 border border-purple-200 rounded-2xl p-6 text-center">
                <Calendar className="w-10 h-10 text-purple-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-800 mb-1">Prazo</h3>
                <p className="text-purple-600 font-bold text-lg">31 de Maio</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
                <DollarSign className="w-10 h-10 text-green-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-800 mb-1">Limite</h3>
                <p className="text-green-600 font-bold text-lg">R$ 81.000/ano</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
                <AlertCircle className="w-10 h-10 text-red-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-800 mb-1">Multa</h3>
                <p className="text-red-600 font-bold text-lg">Mín. R$ 50,00</p>
              </div>
            </div>
          </section>

          {/* Calculadora */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
              📊 Calculadora: Você está dentro do limite?
            </h2>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-200 rounded-2xl p-6 md:p-8"
            >
              <p className="text-gray-600 mb-6">
                Digite seu faturamento total do ano para verificar sua situação:
              </p>
              
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Faturamento Anual (R$)
                  </label>
                  <input
                    type="text"
                    value={faturamento}
                    onChange={(e) => {
                      const valor = e.target.value.replace(/\D/g, '');
                      const valorFormatado = (parseInt(valor || 0) / 100).toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      });
                      setFaturamento(valorFormatado);
                    }}
                    placeholder="R$ 0,00"
                    className="w-full px-4 py-3 border border-purple-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg"
                  />
                </div>
                <motion.button
                  onClick={calcularSituacao}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-8 py-3 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-700 transition-colors md:self-end"
                >
                  Calcular
                </motion.button>
              </div>

              {resultado && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-xl ${
                    resultado.status === 'ok' ? 'bg-green-100 border border-green-300' :
                    resultado.status === 'atencao' ? 'bg-amber-100 border border-amber-300' :
                    'bg-red-100 border border-red-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {resultado.status === 'ok' ? (
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    ) : resultado.status === 'atencao' ? (
                      <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                    )}
                    <div>
                      <p className={`font-bold ${
                        resultado.status === 'ok' ? 'text-green-800' :
                        resultado.status === 'atencao' ? 'text-amber-800' :
                        'text-red-800'
                      }`}>
                        {resultado.mensagem}
                      </p>
                      <p className={`text-sm mt-1 ${
                        resultado.status === 'ok' ? 'text-green-700' :
                        resultado.status === 'atencao' ? 'text-amber-700' :
                        'text-red-700'
                      }`}>
                        {resultado.detalhe}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </section>

          {/* Passo a Passo */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
              Como Fazer a Declaração Anual: Passo a Passo
            </h2>

            <div className="space-y-6">
              {steps.map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl flex items-center justify-center flex-shrink-0 text-white font-bold">
                      {step.number}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-800 text-lg mb-2">{step.title}</h3>
                      <p className="text-gray-600 mb-3">{step.description}</p>
                      <div className="flex items-center gap-2 text-sm text-purple-600 bg-purple-50 px-3 py-2 rounded-lg inline-flex">
                        <Zap className="w-4 h-4" />
                        <span className="font-medium">Dica:</span> {step.dica}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-10 bg-gradient-to-r from-purple-100 to-violet-100 rounded-2xl p-6 md:p-8"
            >
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    😫 Muito complicado?
                  </h3>
                  <p className="text-gray-600">
                    Com o MeuMei, sua declaração é gerada automaticamente com base nas suas notas fiscais. 
                    É só revisar e enviar!
                  </p>
                </div>
                <motion.button
                  onClick={() => navigateTo('https://app.meumei.net/register')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-gradient-to-r from-purple-500 to-violet-600 text-white font-bold rounded-xl shadow-lg whitespace-nowrap"
                >
                  Fazer Declaração Fácil
                </motion.button>
              </div>
            </motion.div>
          </section>

          {/* Erros Comuns */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
              ❌ Erros Comuns na Declaração (Evite!)
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {errosComuns.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white border border-gray-200 rounded-2xl p-6"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <p className="text-red-700 font-medium">{item.erro}</p>
                  </div>
                  <div className="flex items-start gap-3 ml-11">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <p className="text-green-700">{item.solucao}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
              Perguntas Frequentes sobre DASN-SIMEI
            </h2>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="border border-gray-200 rounded-2xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-gray-800 pr-4">{faq.pergunta}</span>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-purple-600 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  <AnimatePresence>
                    {openFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <p className="px-6 pb-6 text-gray-600 leading-relaxed">
                          {faq.resposta}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </section>

          {/* CTA Final */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-500 to-violet-600 rounded-3xl p-8 md:p-12 text-center text-white relative overflow-hidden"
            >
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
              </div>

              <div className="relative z-10">
                <h2 className="text-2xl md:text-4xl font-bold mb-4">
                  Não perca o prazo da sua declaração!
                </h2>
                <p className="text-lg md:text-xl opacity-90 mb-8 max-w-2xl mx-auto">
                  Com o MeuMei, sua declaração anual é gerada automaticamente. 
                  Você só precisa revisar e enviar. Simples assim!
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <motion.button
                    onClick={() => navigateTo('https://app.meumei.net/register')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-4 bg-white text-purple-600 font-bold rounded-xl shadow-lg hover:shadow-xl transition-all"
                  >
                    Começar Agora - É Grátis
                  </motion.button>
                </div>
                <p className="text-sm opacity-75 mt-4">
                  ✓ 7 dias grátis &nbsp; ✓ Declaração automática &nbsp; ✓ Suporte incluído
                </p>
              </div>
            </motion.div>
          </section>

        </div>
      </article>

      {/* Espaço para CTA fixo mobile */}
      <div className="h-24 md:hidden" />

      <Footer />
    </div>
  );
};

export default DeclaracaoAnualMeiPage;